import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconBuilding, IconPlus, IconEdit, IconTrash } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const Departments = () => {
    const [appId, setAppId] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDepartment, setEditingDepartment] = useState(null);
    const [form, setForm] = useState({ name: '', description: '' });

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadDepartments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadDepartments = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/departments/`);
            setDepartments(res.data.departments || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar departamentos' });
        }
    };

    const handleOpenCreate = () => {
        setEditingDepartment(null);
        setForm({ name: '', description: '' });
        setDialogOpen(true);
    };

    const handleOpenEdit = (department) => {
        setEditingDepartment(department);
        setForm({ name: department.name, description: department.description || '' });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name) {
            setStatus({ type: 'error', message: 'Informe o nome do departamento' });
            return;
        }
        try {
            if (editingDepartment) {
                await api.patch(`v1/apps/${appId}/departments/${editingDepartment.id}/`, form);
            } else {
                await api.post(`v1/apps/${appId}/departments/`, form);
            }
            setDialogOpen(false);
            loadDepartments();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar departamento' });
        }
    };

    const handleDelete = async (department) => {
        if (!window.confirm(`Excluir o departamento "${department.name}"?`)) return;
        try {
            await api.delete(`v1/apps/${appId}/departments/${department.id}/`);
            setDepartments((prev) => prev.filter((d) => d.id !== department.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir departamento' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconBuilding}
                gradient="blue"
                title="Departamentos"
                subtitle="Estrutura organizacional usada pelos cadastros de RH"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Novo Departamento
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {departments.map((department) => (
                                <TableRow key={department.id} hover>
                                    <TableCell>{department.name}</TableCell>
                                    <TableCell>{department.description || '—'}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleOpenEdit(department)}>
                                            <IconEdit size={16} />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(department)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {departments.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        Nenhum departamento cadastrado ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingDepartment ? 'Editar departamento' : 'Novo departamento'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nome"
                            fullWidth
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                        />
                        <TextField
                            label="Descrição (opcional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Departments;
